import { useMutation, useQuery } from '@apollo/client';
import { UpdateDomain } from 'Apollo/mutations/organization/UpdateDomain';
import { UpdateOrganizationGuestUsersSecurity } from 'Apollo/mutations/organization/UpdateOrganizationGuestUsersSecurity';
import { GetOrganizationDomains } from 'Apollo/queries/organization/GetOrganizationDomains';
import { GetOrganizationGuestUsersSecurity } from 'Apollo/queries/organization/GetOrganizationGuestUsersSecurity';
import { GetOrganizationSecurityAccessRights } from 'Apollo/queries/organization/GetOrganizationSecurityAccessRights';
import useNotification from 'Hooks/useNotification';
import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ROW_PER_PAGE_DEFAULT } from 'Theme/config';
import OrganizationSecurity from './OrganizationSecurity';

function OrganizationSecurityContainer() {
  const { onError } = useNotification();
  const { organizationCode } = useParams<{ organizationCode: string }>();
  const [rowsPerPage, setRowsPerPage] = useState<number>(ROW_PER_PAGE_DEFAULT);
  const [currentPage, setCurrentPage] = useState<number>(0);

  const isGuestUsersSecurityEnabled = AppSettings.IS_GUEST_USER_ENABLED;

  const { data, loading } = useQuery<
    GetOrganizationDomainsData,
    GetOrganizationDomainsVars
  >(GetOrganizationDomains, {
    fetchPolicy: 'cache-and-network',
    variables: {
      code: organizationCode,
      page: {
        first: rowsPerPage,
        offset: currentPage * rowsPerPage
      }
    },
    onError
  });

  const { data: guestUsersSecurityData } = useQuery<
    GetOrganizationGuestUsersSecurityData,
    GetOrganizationGuestUsersSecurityVars
  >(GetOrganizationGuestUsersSecurity, {
    fetchPolicy: 'cache-and-network',
    variables: { code: organizationCode },
    skip: !isGuestUsersSecurityEnabled,
    onError
  });

  // The caller's rights are a separate concern from the organization's settings, hence a separate query.
  const { data: accessRightsData } = useQuery<
    GetOrganizationSecurityAccessRightsData,
    GetOrganizationSecurityAccessRightsVars
  >(GetOrganizationSecurityAccessRights, {
    fetchPolicy: 'cache-and-network',
    variables: { code: organizationCode },
    onError
  });

  const [updateDomain] = useMutation<
    UpdateOrganizationDomainData,
    UpdateOrganizationDomainVariables
  >(UpdateDomain, { onError });

  const [updateGuestUsersSecurity] = useMutation<
    UpdateOrganizationGuestUsersSecurityData,
    UpdateOrganizationGuestUsersSecurityVariables
  >(UpdateOrganizationGuestUsersSecurity, { onError });

  const handleToggle = useCallback(
    (row: IOrganizationDomain, field: 'forceTotp' | 'forceEmail') => {
      updateDomain({
        variables: {
          code: organizationCode,
          name: row.name,
          input: {
            forceTotp: field === 'forceTotp' ? !row.forceTotp : row.forceTotp,
            forceEmail: field === 'forceEmail' ? !row.forceEmail : row.forceEmail
          }
        },
        refetchQueries: [GetOrganizationDomains]
      });
    },
    [organizationCode, updateDomain]
  );

  // Null while the query is in flight and after an error : the section then renders a skeleton instead of toggles, so
  // no patch can be computed from an unknown state. The error itself is surfaced by the onError notification.
  const guestUsersSecurity = guestUsersSecurityData?.organizationGuestUsersSecurity ?? null;

  const handleGuestUsersToggle = useCallback(
    (field: 'forceTotp' | 'forceEmail') => {
      // Unreachable in practice, the toggles are not rendered until the security is known : this only narrows the type.
      if (guestUsersSecurity == null) {
        return;
      }

      updateGuestUsersSecurity({
        variables: {
          code: organizationCode,
          input: {
            [field]: !guestUsersSecurity[field]
          }
        },
        refetchQueries: [GetOrganizationGuestUsersSecurity]
      });
    },
    [organizationCode, updateGuestUsersSecurity, guestUsersSecurity]
  );

  const allDomains = data?.organizationDomains.edges.map(edge => edge.node) ?? [];
  const domains = allDomains.filter(domain => domain.validated);
  const totalCount = domains.length;

  return (
    <OrganizationSecurity
      loading={loading}
      domains={domains}
      totalCount={totalCount}
      currentPage={currentPage}
      onChangePage={setCurrentPage}
      rowsPerPage={rowsPerPage}
      onChangeRowsPerPage={setRowsPerPage}
      onHandleToggle={handleToggle}
      isGuestUsersSecurityEnabled={isGuestUsersSecurityEnabled}
      guestUsersSecurity={guestUsersSecurity}
      onHandleGuestUsersToggle={handleGuestUsersToggle}
      canEnforceTwoFactor={accessRightsData?.organizationSecurityAccessRights.canEnforceTwoFactor ?? false}
    />
  );
}

export default OrganizationSecurityContainer;
